import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import AccountTreeIcon from "@material-ui/icons/AccountTree";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import { i18n } from "../../translate/i18n";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import FlowBuilderModal from "../../components/FlowBuilderModal";

import MetricsStrip from "../../components/MetricsStrip";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    minWidth: 0,
  },
  contentStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  hero: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  summaryCard: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 16,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(0.75),
  },
  filtersPaper: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filtersTitle: {
    fontWeight: 600,
  },
  filtersActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  tableSearch: {
    width: 320,
    maxWidth: "100%",
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    width: "100%",
    minWidth: 0,
  },
  tableHeadCell: {
    fontWeight: 600,
  },
  flowNameCell: {
    cursor: "pointer",
  },
  flowNameContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  flowIcon: {
    color: theme.palette.text.secondary,
  },
  helperText: {
    opacity: 0.72,
    marginTop: theme.spacing(0.5),
  },
}));

const FlowBuilder = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [reloadFlag, setReloadFlag] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [flows, setFlows] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [flowModalOpen, setFlowModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [duplicateConfirmOpen, setDuplicateConfirmOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const fetchFlows = async () => {
        setLoading(true);

        try {
          const { data } = await api.get("/flowbuilder");
          setFlows(data.flows || []);
        } catch (err) {
          toastError(err);
        } finally {
          setLoading(false);
        }
      };

      fetchFlows();
    }, 250);

    return () => clearTimeout(delayDebounceFn);
  }, [reloadFlag]);

  const refreshFlows = () => {
    setReloadFlag((current) => current + 1);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenFlowModal = () => {
    setSelectedFlow(null);
    setFlowModalOpen(true);
  };

  const handleCloseFlowModal = () => {
    setSelectedFlow(null);
    setFlowModalOpen(false);
  };

  const openEditor = (flowId) => {
    history.push(`/flowbuilder/${flowId}`);
  };

  const handleDeleteFlow = async () => {
    if (!selectedFlow) return;

    try {
      await api.delete(`/flowbuilder/${selectedFlow.id}`);
      toast.success("Fluxo excluido com sucesso");
      setDeleteConfirmOpen(false);
      setSelectedFlow(null);
      refreshFlows();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDuplicateFlow = async () => {
    if (!selectedFlow) return;

    try {
      await api.post("/flowbuilder/duplicate", { flowId: selectedFlow.id });
      toast.success("Fluxo duplicado com sucesso");
      setDuplicateConfirmOpen(false);
      setSelectedFlow(null);
      refreshFlows();
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenMenu = (event, flow) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedFlow(flow);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const filteredFlows = useMemo(() => {
    if (!searchParam) {
      return flows;
    }

    return flows.filter((flow) => {
      const normalizedName = String(flow.name || "").toLowerCase();
      const normalizedStatus = flow.active ? "ativo" : "desativado";

      return (
        normalizedName.includes(searchParam) || normalizedStatus.includes(searchParam)
      );
    });
  }, [flows, searchParam]);

  const summary = useMemo(() => {
    const active = flows.filter((flow) => flow.active).length;

    return {
      total: flows.length,
      active,
      inactive: flows.length - active,
      visible: filteredFlows.length,
    };
  }, [filteredFlows.length, flows]);

  const summaryCards = useMemo(
    () => [
      { key: "flows", label: "Fluxos", value: summary.total },
      { key: "active", label: "Ativos", value: summary.active },
      { key: "inactive", label: "Desativados", value: summary.inactive },
      { key: "visible", label: "Visiveis", value: summary.visible },
    ],
    [summary]
  );


  return (
    <MainContainer className={classes.mainContainer} fullWidth>
      <FlowBuilderModal
        open={flowModalOpen}
        onClose={handleCloseFlowModal}
        aria-labelledby="form-dialog-title"
        flowId={selectedFlow?.id}
        nameWebhook={selectedFlow?.name}
        onSave={refreshFlows}
      />

      <ConfirmationModal
        title={
          selectedFlow
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${selectedFlow.name}?`
            : i18n.t("contacts.confirmationModal.importTitlte")
        }
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setSelectedFlow(null);
        }}
        onConfirm={handleDeleteFlow}
      >
        {selectedFlow
          ? "Tem certeza que deseja excluir este fluxo? As integracoes relacionadas serao perdidas."
          : i18n.t("contacts.confirmationModal.importMessage")}
      </ConfirmationModal>

      <ConfirmationModal
        title={
          selectedFlow
            ? `Deseja duplicar o fluxo ${selectedFlow.name}?`
            : i18n.t("contacts.confirmationModal.importTitlte")
        }
        open={duplicateConfirmOpen}
        onClose={() => {
          setDuplicateConfirmOpen(false);
          setSelectedFlow(null);
        }}
        onConfirm={handleDuplicateFlow}
      >
        {selectedFlow
          ? "Tem certeza que deseja duplicar este fluxo?"
          : i18n.t("contacts.confirmationModal.importMessage")}
      </ConfirmationModal>

      <div className={classes.contentStack}>
        <Box className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.heroTitle}>
              Fluxos de conversa
            </Typography>
          </Box>

          <Box className={classes.heroActions}>
            <Button variant="outlined" onClick={refreshFlows} disabled={loading}>
              Atualizar
            </Button>
            <Button variant="contained" color="primary" onClick={handleOpenFlowModal}>
              Adicionar fluxo
            </Button>
          </Box>
        </Box>

        <MetricsStrip ariaLabel="Indicadores de fluxos de conversa" items={summaryCards} />

        <Paper className={classes.tablePaper} variant="outlined">
          <Box className={classes.tableHeader}>
            <Typography variant="body1" className={classes.tableHeaderTitle}>
              Fluxos operacionais
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
              <TextField
                className={classes.tableSearch}
                variant="outlined"
                size="small"
                placeholder={i18n.t("contacts.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                inputProps={{ "aria-label": "Pesquisar fluxos de conversa" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Chip size="small" label={`${summary.total} cadastrados`} />
              <Chip size="small" label={`${summary.active} ativos`} />
            </Box>
          </Box>

          <TableContainer className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeadCell}>Fluxo</TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    Status
                  </TableCell>
                  <TableCell align="right" className={classes.tableHeadCell}>
                    Acoes
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading &&
                  filteredFlows.map((flow) => (
                    <TableRow key={flow.id} hover>
                      <TableCell
                        className={classes.flowNameCell}
                        onClick={() => openEditor(flow.id)}
                      >
                        <Box className={classes.flowNameContent}>
                          <AccountTreeIcon fontSize="small" className={classes.flowIcon} />
                          <Typography variant="body2">{flow.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center" onClick={() => openEditor(flow.id)}>
                        <Chip
                          size="small"
                          color={flow.active ? "primary" : "default"}
                          variant={flow.active ? "default" : "outlined"}
                          label={flow.active ? "Ativo" : "Desativado"}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          aria-label="acoes do fluxo"
                          onClick={(event) => handleOpenMenu(event, flow)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                {loading && <TableRowSkeleton columns={3} />}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && filteredFlows.length === 0 && (
            <Box p={4} textAlign="center">
              <Typography variant="h6">Nenhum fluxo encontrado</Typography>
              <Typography variant="body2" className={classes.helperText}>
                Ajuste a busca ou cadastre um novo fluxo para continuar.
              </Typography>
            </Box>
          )}
        </Paper>
      </div>

      <Menu
        anchorEl={menuAnchorEl}
        keepMounted
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem
          onClick={() => {
            handleCloseMenu();
            setFlowModalOpen(true);
          }}
        >
          Editar nome
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseMenu();
            if (selectedFlow) {
              openEditor(selectedFlow.id);
            }
          }}
        >
          Editar fluxo
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseMenu();
            setDuplicateConfirmOpen(true);
          }}
        >
          Duplicar
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseMenu();
            setDeleteConfirmOpen(true);
          }}
        >
          Excluir
        </MenuItem>
      </Menu>
    </MainContainer>
  );
};

export default FlowBuilder;
